import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/auth/AuthContext";
import GoogleButton from "@/auth/GoogleButton";
import { PageMeta } from "@/components/MarketingLayout";
import PasswordInput from "@/components/PasswordInput";

export default function Login() {
  const { user, loading, pinLocked, loginGoogle, loginPassword, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user || pinLocked) navigate("/dashboard", { replace: true });
  }, [user, pinLocked, navigate]);
  if (loading) return null;

  const switchMode = (nextMode) => {
    setMode(nextMode);
    setErr("");
    setSearchParams(nextMode === "register" ? { mode: "register" } : {}, { replace: true });
  };

  const submit = async (event) => {
    event.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await loginPassword(email.trim(), password);
      else await register(email.trim(), password, name.trim());
    } catch (error) {
      setErr(error?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-[var(--m-border)] bg-[var(--m-elevated)] px-3.5 py-3 text-sm text-[var(--m-text)] outline-none transition placeholder:text-[var(--m-muted)] focus:border-[var(--m-link)] focus:ring-2 focus:ring-[var(--m-focus)]";

  return (
    <div className="marketing-site min-h-[100dvh] lg:grid lg:grid-cols-[1.08fr_0.92fr]">
      <PageMeta
        title={mode === "login" ? "Sign In | Nothing Before Law" : "Create Account | Nothing Before Law"}
        description="Sign in to or create your Nothing Before Law Case Diary account."
      />

      <aside className="relative hidden min-h-[100dvh] overflow-hidden lg:block">
        <img
          src="/images/marketing/advocates-courthouse-hero.webp"
          alt="Indian advocates reviewing a case diary in a courthouse corridor"
          width="1536"
          height="1024"
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover object-[64%_center]"
        />
        <div className="marketing-login-scrim absolute inset-0" />
        <div className="absolute inset-x-0 bottom-0 p-10 xl:p-14">
          <p className="max-w-lg font-serif text-4xl leading-tight text-[#fffaf4] xl:text-5xl">A clear record for every matter that reaches your desk.</p>
        </div>
      </aside>

      <main className="flex min-h-[100dvh] items-center justify-center px-4 py-10 sm:px-8 lg:px-12">
        <div className="w-full max-w-md">
          <Link to="/" className="marketing-text-link inline-flex items-center gap-2 text-sm">
            <ArrowLeft size={16} weight="bold" /> Back home
          </Link>

          <div className="mt-9 flex items-center gap-3">
            <img src="/logo-nbl-icon.svg" alt="" className="h-11 w-11" />
            <div>
              <div className="font-display text-2xl text-[var(--m-text)]">Nothing Before Law</div>
              <div className="text-xs font-semibold tracking-[0.08em] text-[var(--m-muted)]">CASE DIARY</div>
            </div>
          </div>

          <h1 className="mt-9 font-serif text-4xl text-[var(--m-text)]">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-2 text-sm leading-6 text-[var(--m-muted)]">
            {mode === "login" ? "Sign in to open your Case Diary." : "Start organising your practice in a few minutes."}
          </p>

          {searchParams.get("reset") && mode === "login" && (
            <div className="mt-6 rounded-lg border border-[var(--m-border)] bg-[var(--m-elevated)] px-4 py-3 text-sm text-[var(--m-text)]" data-testid="reset-done">
              Your password has been changed. Sign in with the new one.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            {mode === "register" && (
              <div className="grid gap-2">
                <label htmlFor="account-name" className="text-sm font-medium text-[var(--m-text)]">Full name</label>
                <input
                  id="account-name"
                  className={inputCls}
                  value={name}
                  required
                  autoComplete="name"
                  onChange={(event) => setName(event.target.value)}
                  data-testid="login-name"
                />
              </div>
            )}
            <div className="grid gap-2">
              <label htmlFor="account-email" className="text-sm font-medium text-[var(--m-text)]">Email address</label>
              <input
                id="account-email"
                className={inputCls}
                type="email"
                value={email}
                required
                autoComplete="email"
                onChange={(event) => setEmail(event.target.value)}
                data-testid="login-email"
              />
            </div>
            <div className="grid gap-2">
              <div className="flex items-baseline justify-between gap-3">
                <label htmlFor="account-password" className="text-sm font-medium text-[var(--m-text)]">Password</label>
                {mode === "login" && (
                  <Link to="/forgot-password" className="marketing-text-link text-xs" data-testid="forgot-password-link">
                    Forgot password?
                  </Link>
                )}
              </div>
              <PasswordInput
                id="account-password"
                className={inputCls}
                value={password}
                required
                minLength={mode === "register" ? 8 : undefined}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                aria-describedby={mode === "register" ? "password-help" : undefined}
                onChange={(event) => setPassword(event.target.value)}
                data-testid="login-password"
              />
              {mode === "register" && <p id="password-help" className="text-xs text-[var(--m-muted)]">Use at least 8 characters.</p>}
            </div>
            {err && <div className="marketing-form-error" role="alert" data-testid="login-error">{err}</div>}
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit"
              className="marketing-primary-button w-full"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="mt-5 text-sm text-[var(--m-muted)]">
            {mode === "login" ? (
              <>New here? <button type="button" className="marketing-inline-button" onClick={() => switchMode("register")} data-testid="switch-register">Create account</button></>
            ) : (
              <>Already have an account? <button type="button" className="marketing-inline-button" onClick={() => switchMode("login")} data-testid="switch-login">Sign in</button></>
            )}
          </div>

          <GoogleButton onCredential={loginGoogle} onError={setErr} />

          <p className="mt-8 text-xs leading-5 text-[var(--m-muted)]">
            By continuing, you agree to our <Link className="marketing-footer-link" to="/terms">Terms and Conditions</Link> and acknowledge our <Link className="marketing-footer-link" to="/privacy">Privacy Policy</Link>.
          </p>
        </div>
      </main>
    </div>
  );
}
